import * as z from "zod";
import { UseFormReturn } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form.tsx";
import { Input } from "@/components/ui/input.tsx";
import { InfoIcon } from "lucide-react";
import { H4 } from "@/components/ui/h4.tsx";
import {
  Tooltip,
  TooltipTrigger,
  TooltipProvider,
  TooltipContent,
} from "@/components/ui/tooltip";
import { BadgeInput } from "@/components/ui/badgeInput";
import { Button } from "@/components/ui/button.tsx";
import { PlusIcon, XIcon } from "lucide-react";
import { TwitterConnectionStatus } from "@/components/manage-personas/TwitterConnectionStatus.tsx";
import { fetchSuggestedKOLs, type Persona } from "@/api/personas";
import { useState } from "react";
import { LoadingButton } from "@/components/LoadingButton";
import { toast } from "@/hooks/use-toast";
import { InstagramConnectionStatus } from "../InstagramConnectionStatus";
import { useAuth } from "@/hooks/use-auth";
import { Role } from "@/components/AuthorizationProvider";

export const accountConfigFormSchema = z.object({
  telegram: z.string().optional(), // Optional because Telegram might not always be provided
  usernames_to_engage: z.array(z.string()).default([]), // Defaults to an empty array if not provided
  twitter_lists: z.array(z.string()).default([]), // Defaults to an empty array if not provided
  persona_id: z.string().optional(), // Optional, added in case it's needed for validation
});

export type PersonaAccountConfig = z.infer<typeof accountConfigFormSchema>;

export function AccountConfigStep({
  readOnly,
  form,
  personaData,
}: {
  readOnly: boolean;
  form: UseFormReturn<PersonaAccountConfig>;
  personaData: Persona;
}) {
  const [kolsLoading, setKolsLoading] = useState(false);
  const { hasAtLeastRole } = useAuth();

  const isAdmin = hasAtLeastRole(Role.TenantAdmin);
  async function fetchAndAddSuggestedKOLs(personaId: string) {
    setKolsLoading(true);
    try {
      const suggestedKOLs = await fetchSuggestedKOLs(personaId);
      const currentKOLs = form.getValues()["usernames_to_engage"];
      const uniqueKOLs = Array.from(
        new Set([...currentKOLs, ...suggestedKOLs]),
      );
      form.setValue("usernames_to_engage", uniqueKOLs);
    } catch (error) {
      console.error("Error fetching suggested KOLs:", error);
      toast({ description: "Error fetching suggested KOLs" });
    } finally {
      setKolsLoading(false);
    }
  }

  return (
    <Form {...form}>
      <form className="flex flex-col gap-y-6">
        <H4 className="text-primary mb-2">
          Configure Your Agent's Social Presence
        </H4>
        <p className="text-sm text-white mt-0 mb-5 align-top">
          Link social accounts and configure your AI agent's engagement settings
          to optimize its interactions.
        </p>

        {/* First Row: Twitter and Telegram */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4">
          <FormItem>
            <FormLabel>Twitter Status</FormLabel>
            <FormControl>
              <TwitterConnectionStatus
                persona={personaData}
                twitterHandle={personaData.twitter}
                isConnected={!!personaData?.twitter}
              />
            </FormControl>
          </FormItem>
          {isAdmin && (
            <FormItem>
              <FormLabel>Instagram Status</FormLabel>
              <FormControl>
                <InstagramConnectionStatus
                  persona={personaData}
                  instagramData={personaData.instagram_data}
                  isConnected={!!personaData?.instagram_data}
                />
              </FormControl>
            </FormItem>
          )}

          <FormField
            control={form.control}
            name="telegram"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Telegram Handle</FormLabel>
                <FormControl>
                  <Input
                    disabled={readOnly}
                    {...field}
                    placeholder="@example"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Second Row: Users to Engage and Twitter Lists */}
        <div className="grid grid-cols-1 sm:grid-cols-[2fr,1fr] gap-x-4 gap-y-4">
          {/* Users to Engage */}
          <FormField
            control={form.control}
            name="usernames_to_engage"
            render={({ field }) => (
              <FormItem>
                <div className="flex flex-row items-center gap-x-2">
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex flex-row items-center w-max">
                          <FormLabel required>
                            Key Opinion Leaders (KOLs)
                          </FormLabel>
                          <InfoIcon className="pl-1 w-4" />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-64">
                          Add the usernames of Key Opinion Leaders (KOLs) your
                          agent should engage with.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <LoadingButton
                    type="button"
                    className=""
                    variant="outline"
                    loading={kolsLoading}
                    onClick={() => {
                      fetchAndAddSuggestedKOLs(personaData.persona_id);
                    }}
                  >
                    Add suggested KOLs
                  </LoadingButton>
                </div>
                <FormControl>
                  <BadgeInput
                    disabled={readOnly}
                    value={field.value || []} // Ensure it's an array
                    onChange={(newTags) => field.onChange(newTags)}
                    placeholder="Add a KOL"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Twitter Lists */}
          <FormField
            control={form.control}
            name="twitter_lists"
            render={({ field }) => (
              <FormItem>
                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex flex-row items-center w-max">
                        <FormLabel required>Twitter Lists</FormLabel>
                        <InfoIcon className="pl-1 w-4" />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-64">
                        Add URLs of Twitter lists with target followers & KOLs
                        to engage with.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <FormControl>
                  <div className="flex flex-col gap-y-2">
                    {(field.value || []).map((url, index) => (
                      <div
                        key={index}
                        className="flex flex-row items-center gap-x-2"
                      >
                        <Input
                          value={url}
                          disabled={readOnly}
                          onChange={(e) => {
                            const updated = [...field.value];
                            updated[index] = e.target.value;
                            field.onChange(updated);
                          }}
                          placeholder="https://x.com/i/lists/1587987762908651120"
                        />
                        {!readOnly && (
                          <Button
                            type="button"
                            variant="ghost"
                            onClick={() => {
                              const updated = [...field.value];
                              updated.splice(index, 1);
                              field.onChange(updated);
                            }}
                          >
                            <XIcon className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    {!readOnly && (
                      <Button
                        className="flex flex-row gap-x-1 items-center"
                        type="button"
                        variant="ghost"
                        onClick={() =>
                          field.onChange([...(field.value || []), ""])
                        }
                      >
                        <PlusIcon className="w-4 h-4" />
                        Add
                      </Button>
                    )}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </form>
    </Form>
  );
}
